<template>
	<header class="bg-white shadow-sm sticky top-0 z-50">
		<div class="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
			<RouterLink
				to="/"
				class="flex items-center text-base font-bold text-gray-900 hover:text-blue-600 active:scale-90 active:opacity-60 transition-[color,opacity,transform] duration-75"
				>Badminton Americano</RouterLink
			>

			<div class="flex items-center gap-2">
				<div class="relative" @click.stop>
					<button
						@click="langOpen = !langOpen"
						class="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
						:aria-label="t('header.change_language')"
					>
						<img
							:src="locale === 'ru' ? flagRu : flagEn"
							class="w-5 h-auto rounded-[2px] ring-1 ring-black/15 shrink-0"
							aria-hidden="true"
						/>
						{{ locale.toUpperCase() }}
					</button>
					<div
						v-if="langOpen"
						class="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[90px] py-1"
					>
						<button
							v-for="lang in ['en', 'ru'] as const"
							:key="lang"
							@click="setLang(lang)"
							class="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors"
							:class="locale === lang ? 'font-semibold text-blue-600' : 'text-gray-700'"
						>
							<img
								:src="lang === 'ru' ? flagRu : flagEn"
								class="w-5 h-auto rounded-[2px] ring-1 ring-black/15 shrink-0"
								aria-hidden="true"
							/>
							{{ lang.toUpperCase() }}
						</button>
					</div>
				</div>

				<div v-if="!userStore.loading">
					<template v-if="userStore.isAuthenticated">
						<div class="flex items-center gap-3">
							<RouterLink
								to="/account"
								:aria-label="t('header.my_account')"
								class="flex items-center gap-1 text-sm text-gray-600 max-w-[130px] hover:text-blue-600 active:scale-90 active:opacity-60 transition-[color,opacity,transform] duration-75"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									class="w-4 h-4 shrink-0"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									stroke-width="2"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									/>
								</svg>
								<span class="truncate">{{ shortName }}</span>
							</RouterLink>
							<button
								@click="userStore.logout()"
								class="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-90 active:opacity-60 transition-[color,background-color,opacity,transform] duration-75 text-gray-400 hover:text-gray-600"
								:aria-label="t('header.log_out')"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									class="w-4 h-4"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									stroke-width="2"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
									/>
								</svg>
							</button>
						</div>
					</template>
					<template v-else>
						<button
							@click="handleLogin"
							class="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
						>
							{{ t('header.log_in') }}
						</button>
					</template>
				</div>
			</div>
		</div>
	</header>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useUserStore } from '@/stores/user';
import flagRu from '@/assets/flags/ru.svg';
import flagEn from '@/assets/flags/en.svg';

const route = useRoute();
const userStore = useUserStore();
const { t, locale } = useI18n();

const shortName = computed(() => userStore.displayName?.split(' ')[0] ?? '');

function handleLogin() {
	userStore.login(route.fullPath);
}

const langOpen = ref(false);

function setLang(lang: 'en' | 'ru'): void {
	locale.value = lang;
	localStorage.setItem('lang', lang);
	langOpen.value = false;
}

function closeLang(): void {
	langOpen.value = false;
}

onMounted(() => document.addEventListener('click', closeLang));
onUnmounted(() => document.removeEventListener('click', closeLang));
</script>
